import { ref, computed, watch, onUnmounted } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { t } from '@/utils/i18n-helper';
import { usePaymentMethodInfo } from '@/composables/front/perfil/usePaymentMethodInfo';
import { use3DSPolling } from '@/composables/front/perfil/use3DSPolling';

/**
 * Orquestador del flujo de pago de plazos.
 *
 * Soporta dos modos de pago:
 * - **Cantera** (supports3DS=false): callback onConfirm(payload) — el padre gestiona el pago.
 * - **Abonos**  (supports3DS=true):  dispatch interno, iframe 3DS + polling, emit('pago-completado').
 *
 * @param {Object} options
 * @param {import('vue').Ref<boolean>} options.show
 * @param {import('vue').Ref<Array>} options.plazos
 * @param {boolean} options.supports3DS — activa flujo abonos con iframe 3DS y dispatch interno
 * @param {import('vue').Ref<string>} [options.tipoVenta] — requerido si supports3DS=true
 * @param {Function} [options.emit] — requerido si supports3DS=true (el emit del componente)
 * @param {import('vue').Ref<boolean>} [options.loading] — loading externo del padre (bloquea cierre)
 * @param {import('vue').Ref} [options.pagoFormRef] — ref al componente PagoPaycometForm
 * @param {(payload: Object) => void} [options.onConfirm] — requerido si supports3DS=false
 */
export function usePlazosPaymentFlow({ show, plazos, supports3DS = false, tipoVenta, emit, loading, pagoFormRef, onConfirm: onConfirmCallback }) {
    // ─── Terms (inlined from usePaymentTerms) ─────────────────────────────
    const terminosValor = ref({ privacidad: false, condiciones: false, comunicaciones: false, plazos: false });
    const mostrarErrorTerminos = ref(false);
    const terminosAceptados = computed(() =>
        terminosValor.value.privacidad && terminosValor.value.condiciones && terminosValor.value.plazos
    );
    const resetTerms = () => {
        terminosValor.value = { privacidad: false, condiciones: false, comunicaciones: false, plazos: false };
        mostrarErrorTerminos.value = false;
    };

    // ─── Method selection (inlined from usePaymentMethodSelection) ──────────
    const metodoPago = ref('guardada');
    const onSelectGuardada = () => { metodoPago.value = 'guardada'; };
    const onSelectNueva = () => { metodoPago.value = 'nueva'; };
    const resetMethod = () => { metodoPago.value = 'guardada'; };

    // ─── Sub-composables ─────────────────────────────────────────────────────
    const paymentInfo = usePaymentMethodInfo();

    // ─── Flow state ───────────────────────────────────────────────────────────
    const viewMode = ref('terms');
    const jetToken = ref('');
    const procesandoPago = ref(false);
    const paycometTimeout = ref(null);
    const errorMsg = ref('');
    const iframeUrl = ref('');
    const guardarTarjeta = ref(false);

    // ─── 3DS polling (extraído a composable dedicado) ──────────────────────
    const polling = use3DSPolling({
        iframeUrl,
        procesandoPago,
        errorMsg,
        onResetForm: () => resetForm(),
        getPlazos: () => plazos.value,
        getTipoVenta: () => tipoVenta?.value || 'plazosAbono',
        emit,
    });

    // ─── Computed ─────────────────────────────────────────────────────────────
    const canConfirm = computed(() => {
        if (procesandoPago.value || paymentInfo.cargandoInfoPago.value) return false;
        if (viewMode.value === 'terms') return terminosAceptados.value;
        if (supports3DS && iframeUrl.value) return false;
        return true;
    });

    // ─── Emit helpers ─────────────────────────────────────────────────────────
    const emitConfirm = () => {
        if (supports3DS) return;
        onConfirmCallback({
            plazoIds: plazos.value.map(p => p.id),
            jetToken: metodoPago.value === 'nueva' ? jetToken.value : null,
            guardarTarjeta: guardarTarjeta.value,
        });
    };

    // ─── Abonos: pago con tarjeta guardada ────────────────────────────────────
    const procesarPagoGuardada = async() => {
        if (!supports3DS) return;
        procesandoPago.value = true;
        errorMsg.value = '';
        try {
            const res = await store.dispatch(
                config.getVuexAlias('front.perfil.procesar-pago-plazos'),
                { tid: paymentInfo.tid.value, plazos: plazos.value.map(p => p.id), tipo_venta: tipoVenta.value, filters: {} }
            );
            if (!res.data?.error) {
                emit('pago-completado');
                resetForm();
            } else {
                errorMsg.value = res.data?.message || t('perfil.error_pago_plazos');
            }
        } catch (e) {
            errorMsg.value = e?.response?.data?.message || t('perfil.error_pago_plazos');
        } finally {
            procesandoPago.value = false;
        }
    };

    // ─── Abonos: callback tras token de Paycomet ──────────────────────────────
    const onPagoCompletado = async jetTokenValue => {
        if (!supports3DS) return;
        clearTimeout(paycometTimeout.value);
        if (!jetTokenValue) {
            procesandoPago.value = false;
            errorMsg.value = t('perfil.error_procesar_pago_reintentar', {}, 'Error al procesar el pago. Intentalo de nuevo.');
            return;
        }
        try {
            const res = await store.dispatch(
                config.getVuexAlias('front.perfil.procesar-pago-plazos'),
                {
                    filters: {},
                    jetToken: jetTokenValue,
                    tid: paymentInfo.tid.value,
                    plazos: plazos.value.map(p => p.id),
                    guardarTarjeta: guardarTarjeta.value,
                    tipo_venta: tipoVenta.value,
                }
            );
            const response = res.data?.response || res.data;
            if (response.challengeUrl) {
                iframeUrl.value = response.challengeUrl;
                polling.start3DSPolling(paymentInfo.tid.value, jetTokenValue);
            } else if (response.compraFinalizada || !res.data?.error) {
                procesandoPago.value = false;
                emit('pago-completado');
                resetForm();
            } else {
                procesandoPago.value = false;
                errorMsg.value = res.data?.message || t('perfil.error_pago_plazos');
            }
        } catch (e) {
            procesandoPago.value = false;
            errorMsg.value = e?.response?.data?.message || t('perfil.error_pago_plazos');
        }
    };

    // ─── Cantera: callback tras token de Paycomet ─────────────────────────────
    const onPayToken = token => {
        clearTimeout(paycometTimeout.value);
        jetToken.value = token;
        emitConfirm();
    };

    // ─── Submit con tarjeta nueva ─────────────────────────────────────────────
    const submitNewCard = ({ titular, mes, ano }) => {
        if (!titular || !mes || !ano) {
            errorMsg.value = t('perfil.completar_datos_tarjeta');
            return;
        }

        procesandoPago.value = true;
        errorMsg.value = '';
        clearTimeout(paycometTimeout.value);
        paycometTimeout.value = setTimeout(() => {
            if (procesandoPago.value) {
                procesandoPago.value = false;
                errorMsg.value = t('perfil.error_procesar_tarjeta');
            }
        }, 15000);

        if (pagoFormRef?.value) {
            const form = pagoFormRef.value.$el;
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
    };

    // ─── Flow actions ────────────────────────────────────────────────────────
    const goToPayment = async() => {
        if (!terminosAceptados.value) {
            mostrarErrorTerminos.value = true;
            return;
        }
        const metodo = await paymentInfo.cargarInfoPago(err => { errorMsg.value = err; });
        if (metodo) metodoPago.value = metodo;
        viewMode.value = 'payment';
    };

    // ─── Modal lifecycle ─────────────────────────────────────────────────────
    const resetForm = () => {
        resetTerms();
        resetMethod();
        paymentInfo.resetPaymentInfo();
        viewMode.value = 'terms';
        jetToken.value = '';
        procesandoPago.value = false;
        clearTimeout(paycometTimeout.value);
        errorMsg.value = '';
        iframeUrl.value = '';
        guardarTarjeta.value = false;
        polling.stop3DSPolling();
    };

    const onClose = () => {
        if (loading?.value || paymentInfo.cargandoInfoPago.value || procesandoPago.value) return;
        if (viewMode.value === 'payment') {
            viewMode.value = 'terms';
            errorMsg.value = '';
            return;
        }
        resetForm();
        emit('close');
    };

    const onConfirm = async cardData => {
        if (paymentInfo.cargandoInfoPago.value || procesandoPago.value) return;

        if (viewMode.value === 'terms') {
            if (!terminosAceptados.value) {
                mostrarErrorTerminos.value = true;
                return;
            }
            await goToPayment();
            return;
        }

        if (metodoPago.value === 'guardada') {
            if (supports3DS) {
                await procesarPagoGuardada();
                return;
            }
            emitConfirm();
            return;
        }

        if (metodoPago.value === 'nueva') {
            if (cardData) {
                submitNewCard(cardData);
            }
            return;
        }
    };

    // ─── Lifecycle ───────────────────────────────────────────────────────────
    watch(show, isVisible => {
        if (!isVisible) resetForm();
    });

    onUnmounted(() => {
        clearTimeout(paycometTimeout.value);
        polling.stop3DSPolling();
    });

    // ─── Return ──────────────────────────────────────────────────────────────
    return {
        // Terms
        terminosValor,
        mostrarErrorTerminos,
        terminosAceptados,
        // Flow
        viewMode,
        metodoPago,
        tarjetaGuardada: paymentInfo.tarjetaGuardada,
        tarjetaLast4: paymentInfo.tarjetaLast4,
        jetId: paymentInfo.jetId,
        cargandoInfoPago: paymentInfo.cargandoInfoPago,
        procesandoPago,
        errorMsg,
        guardarTarjeta,
        canConfirm,
        iframeUrl,
        tid: paymentInfo.tid,
        // Actions
        onClose,
        onConfirm,
        goToPayment,
        onSelectGuardada,
        onSelectNueva,
        onPayToken,
        onPagoCompletado,
        resetForm,
    };
}
